/**
 * @module Routes.Facility
 *
 * The one Admin Settings tab list. It replaces the copies in each Admin
 * Settings page.
 */
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { UserContext, UserContextType } from "app/contexts/UserContext";
import { FacilityModel } from "app/models/scheduling/FacilityModel";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";

export type AdminSettingsTabKey =
  | "general"
  | "landingPage"
  | "users"
  | "bookingOptions"
  | "resources"
  | "holidays";

export interface AdminSettingsTab {
  key: AdminSettingsTabKey;
  label: string;
  path: string;
  isActive: boolean;
}

type Translate = (key: string) => string;

/**
 * The tabs in order. Booking Options and Resources need their facility flag
 * and an admin. Each Admin Settings page shows the same list.
 */
export const adminSettingsTabs = (
  user: UserContextType,
  facility: FacilityModel | undefined,
  active: AdminSettingsTabKey,
  t: Translate,
): AdminSettingsTab[] => {
  const flags = facility?.featureFlags;
  const facilityAdminTab = (flag: boolean | undefined) =>
    !!user.behavesLikeAdmin && flag === true;

  const tabs: Array<Omit<AdminSettingsTab, "isActive"> & { show: boolean }> = [
    { key: "general", label: t("General"), path: "/manage_facility", show: true },
    {
      key: "landingPage",
      label: t("Facility Landing Page"),
      path: "/manage_facility/landing_page",
      show: true,
    },
    { key: "users", label: t("Users"), path: "/manage_facility/users", show: true },
    {
      key: "bookingOptions",
      label: t("Booking Options"),
      path: "/manage_facility/booking_options",
      show: facilityAdminTab(flags?.facilityLevelBookingOptions),
    },
    {
      key: "resources",
      label: t("Resources"),
      path: "/manage_facility/resources",
      show: facilityAdminTab(flags?.resourceManagement),
    },
    {
      key: "holidays",
      label: t("Holidays"),
      path: "/manage_facility/holidays",
      show: true,
    },
  ];

  return tabs
    .filter((tab) => tab.show)
    .map(({ key, label, path }) => ({ key, label, path, isActive: key === active }));
};

/** The Admin Settings tabs for the signed-in user, with `active` selected. */
export const useAdminSettingsTabs = (
  active: AdminSettingsTabKey,
): AdminSettingsTab[] => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const currentFacility = useCurrentFacility();

  return useMemo(
    () => adminSettingsTabs(user, currentFacility.data, active, t),
    [user, currentFacility.data, active, t],
  );
};
